import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { openWebCenterDecisionCall } from '@/lib/analytics';
import {
  ArrowRight,
  Calendar,
  Layers,
  FileCode,
  GitBranch,
  Database,
  CheckCircle2,
} from 'lucide-react';
import Navigation from '@/components/Navigation';
import SEO from '@/components/SEO';
import Breadcrumbs from '@/components/Breadcrumbs';
import WebCenterEngagementHistory from '@/components/WebCenterEngagementHistory';
import LastUpdated from '@/components/LastUpdated';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { FOUNDER, founderPersonLd } from '@/lib/founder';

const FIPSA_COMPONENTS = [
  {
    icon: FileCode,
    title: 'WebCenter Imaging (WCI)',
    detail:
      'The invoice imaging layer — capture, storage, and retrieval of invoice images linked to EBS Payables transactions.',
  },
  {
    icon: FileCode,
    title: 'WebCenter Forms Recognition (WFR)',
    detail:
      'The data extraction layer — per-supplier templates that recognize invoice fields, with a verifier queue for low-confidence extractions.',
  },
  {
    icon: GitBranch,
    title: 'Oracle SOA Suite composites',
    detail:
      'The workflow orchestration — BPEL composites that route invoices through approval, exception handling, and posting steps.',
  },
  {
    icon: Database,
    title: 'EBS Payables integration',
    detail:
      'The system-of-record integration — codified connector that posts approved invoices into Oracle E-Business Suite Payables via the Open Interface.',
  },
];

const MODERNIZATION_MAPPING = [
  {
    layer: 'Invoice imaging',
    fipsa: 'WebCenter Imaging (WCI) — Oracle UCM repository',
    modern: 'OCI Object Storage with signed-URL retrieval; image link preserved to ERP transaction',
  },
  {
    layer: 'Data extraction',
    fipsa: 'WebCenter Forms Recognition (WFR) — per-supplier templates + verifier queue',
    modern: 'AI extraction with confidence scoring per field; learns from corrections; no template maintenance',
  },
  {
    layer: 'Workflow',
    fipsa: 'Oracle SOA Suite BPEL composites',
    modern: 'Configuration-driven workflow with per-approver queues, tolerance rules, and exception handling',
  },
  {
    layer: 'Non-PO coding',
    fipsa: 'Oracle ADF custom form (development-heavy to change)',
    modern: 'Configurable coding screen — no ADF development cycle',
  },
  {
    layer: 'EBS integration',
    fipsa: 'Direct EBS Payables Open Interface',
    modern: 'Same Oracle Payables Open Interface — same Oracle-supported pattern',
  },
  {
    layer: 'Fusion integration',
    fipsa: 'Not native — FIPSA targets EBS only',
    modern: 'Oracle Integration Cloud (OIC) + FBDI for Fusion deployment',
  },
];

const FAQS = [
  {
    q: 'What is FIPSA in Oracle WebCenter AP?',
    a: 'FIPSA — the Financial Imaging Processing Solutions Accelerator — is an Oracle-published accelerator that codifies the WebCenter AP imaging workflow for Oracle E-Business Suite Payables. It combines WebCenter Imaging, Forms Recognition, SOA Suite, and EBS Payables integration into a deployable reference implementation. FIPSA was the standard way Oracle customers ran AP automation on EBS through the WebCenter era.',
  },
  {
    q: 'Why modernize FIPSA?',
    a: 'FIPSA is bound to the Oracle Fusion Middleware 12c support lifecycle — Premier Support ends December 2026, Extended Support ends December 2027. FIPSA implementations are also constrained by the underlying architecture: per-supplier WFR templates that require ongoing maintenance, BPEL workflow that requires SOA expertise to change, and ADF non-PO coding forms that turn small changes into development projects. Modernization replaces these layers with AI-driven, configuration-driven equivalents.',
  },
  {
    q: 'Does modernizing FIPSA mean leaving Oracle EBS?',
    a: 'No. EZ Cloud deploys on Oracle E-Business Suite using the same Oracle Payables Open Interface that FIPSA uses — same Oracle-supported pattern, no base-table writes. The customer keeps EBS as system of record. The AP automation layer changes; the ERP does not.',
  },
  {
    q: 'How does EZ Cloud handle the FIPSA workflow shape?',
    a: 'EZ Cloud absorbs the FIPSA workflow shape into a native AI pipeline. Forms Recognition templates become AI extraction with learning. SOA composites become configuration-driven workflow rules. WCI image storage becomes OCI Object Storage with signed-URL retrieval. ADF coding forms become a configurable web UI. The business logic — approval routing, hold codes, tolerance rules — is read directly from your existing FIPSA implementation and translated forward.',
  },
  {
    q: 'How long does FIPSA modernization typically take?',
    a: 'Typical timeline is 4-9 months end to end, depending on customization depth, supplier master complexity, and approval workflow rules. The Modernization Assessment phase (where Andrew reads the existing FIPSA implementation directly) typically runs 2-3 weeks and produces the forward-modernization plan that scopes the implementation.',
  },
  {
    q: 'What if our FIPSA implementation has heavy customization?',
    a: 'Heavy customization is the norm, not the exception. WebCenter AP implementations grow customizations over a decade — bespoke BPEL composites, ADF customizations, custom hold codes, supplier-specific routing rules. The Modernization Assessment reads the customizations directly from your running system. The forward plan accounts for each one explicitly: which translate forward as-is, which simplify in a modern model, which can be eliminated.',
  },
];

const ModernizeWebCenterFipsa = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQS.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };

  const personSchema = { '@context': 'https://schema.org', ...founderPersonLd };

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'TechArticle',
    headline: 'Modernize FIPSA — the Oracle Financial Imaging Processing Solutions Accelerator',
    description:
      'FIPSA modernization for Oracle WebCenter AP customers on E-Business Suite. How EZ Cloud absorbs the FIPSA workflow shape into a modern AI-driven AP automation platform while preserving Oracle EBS integration patterns.',
    author: { '@id': 'https://ezcloud.ai/about#andrew-blackman' },
    publisher: { '@id': 'https://ezcloud.ai/#organization' },
    datePublished: '2026-06-29',
    dateModified: '2026-06-29',
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': 'https://ezcloud.ai/solutions/modernize-webcenter-fipsa',
    },
    about: [
      { '@type': 'Thing', name: 'FIPSA' },
      { '@type': 'Thing', name: 'Oracle WebCenter Imaging' },
      { '@type': 'Thing', name: 'Oracle WebCenter Forms Recognition' },
      { '@type': 'Thing', name: 'Oracle SOA Suite' },
      { '@type': 'Thing', name: 'Oracle E-Business Suite Payables' },
    ],
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Modernize FIPSA — Oracle Financial Imaging Processing Solutions Accelerator | EZ Cloud"
        description="FIPSA modernization for Oracle WebCenter AP customers. How EZ Cloud absorbs the Financial Imaging Processing Solutions Accelerator workflow into a modern AI-driven AP automation platform on Oracle E-Business Suite or Oracle Fusion Cloud ERP."
        canonical="https://ezcloud.ai/solutions/modernize-webcenter-fipsa"
        breadcrumbs={[
          { name: 'Home', url: 'https://ezcloud.ai/' },
          { name: 'Solutions', url: 'https://ezcloud.ai/solutions/modernize-oracle-webcenter' },
          {
            name: 'Modernize FIPSA',
            url: 'https://ezcloud.ai/solutions/modernize-webcenter-fipsa',
          },
        ]}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(personSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
      <Navigation />
      <Breadcrumbs />
      <LastUpdated date="2026-06-29" />

      <main>
        {/* Hero */}
        <section className="bg-hero-gradient text-white pt-16 pb-20 relative overflow-hidden">
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-0 left-0 w-1/2 h-full bg-gradient-to-br from-primary/20 to-transparent" />
            <div className="absolute top-20 right-20 w-96 h-96 bg-accent/10 rounded-full blur-3xl" />
          </div>
          <div className="container mx-auto px-4 max-w-5xl relative">
            <div className="flex items-center gap-2 text-accent text-sm font-semibold uppercase tracking-wide mb-4">
              <Layers className="w-4 h-4" aria-hidden />
              FIPSA Modernization
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6">
              Modernize FIPSA.
              <span className="block text-accent mt-3">
                Keep the workflow shape. Lose the maintenance burden.
              </span>
            </h1>
            <p className="text-xl md:text-2xl text-white/80 max-w-3xl mb-8">
              The Financial Imaging Processing Solutions Accelerator is the canonical Oracle
              WebCenter AP implementation on E-Business Suite. EZ Cloud reads your FIPSA
              implementation directly and translates the business logic forward to a modern
              AI-driven AP automation platform.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                size="lg"
                variant="accent"
                className="font-semibold"
                onClick={() => openWebCenterDecisionCall('webcenter-page')}
              >
                <Calendar className="mr-2 w-5 h-5" aria-hidden />
                Book a FIPSA Modernization Call
                <ArrowRight className="ml-2 w-5 h-5" aria-hidden />
              </Button>
              <Button size="lg" variant="outline-on-dark" asChild>
                <Link to="/solutions/modernize-oracle-webcenter">
                  See the full modernization path
                </Link>
              </Button>
            </div>
          </div>
        </section>

        {/* TL;DR */}
        <section className="py-14 bg-white border-b border-gray-100">
          <div className="container mx-auto px-4 max-w-4xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3">
              What FIPSA is
            </p>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-5 leading-tight">
              The Financial Imaging Processing Solutions Accelerator, defined
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed">
              <p>
                <strong className="text-gray-900">
                  FIPSA — the Financial Imaging Processing Solutions Accelerator — is an
                  Oracle-published accelerator that codifies the Oracle WebCenter AP imaging
                  workflow for Oracle E-Business Suite Payables.
                </strong>{' '}
                It combines four Oracle products into a deployable reference implementation:
                WebCenter Imaging (invoice image capture and storage), WebCenter Forms Recognition
                (data extraction with per-supplier templates), Oracle SOA Suite (BPEL workflow
                composites), and the EBS Payables Open Interface (system-of-record posting).
              </p>
              <p>
                FIPSA modernization is the work of replacing the FIPSA stack with a modern AI-driven
                AP automation platform while preserving the Oracle support posture, audit trail,
                and EBS integration patterns. EZ Cloud reads the existing FIPSA implementation
                directly — BPEL composites, WFR templates, WCI configuration, ADF customizations,
                and the EBS Payables integration code — and translates the business logic forward
                to the modern platform. The customer retains Oracle E-Business Suite as system of
                record.
              </p>
            </div>
          </div>
        </section>

        {/* FIPSA components */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4 max-w-5xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              The FIPSA stack
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              Four Oracle products, working in concert
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              Each layer has its own configuration model, customization patterns, and lifecycle
              dependencies. EZ Cloud reads all four directly during a Modernization Assessment.
            </p>
            <div className="grid sm:grid-cols-2 gap-6">
              {FIPSA_COMPONENTS.map(({ icon: Icon, title, detail }) => (
                <Card key={title} className="p-6 bg-white border border-gray-200 shadow-sm">
                  <div className="flex items-start gap-4">
                    <div className="w-11 h-11 rounded-lg bg-accent/20 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-5 h-5 text-accent" aria-hidden />
                    </div>
                    <div>
                      <h3 className="text-gray-900 font-semibold mb-1">{title}</h3>
                      <p className="text-sm text-gray-600 leading-relaxed">{detail}</p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Modernization mapping table */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4 max-w-6xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              The modernization mapping
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              FIPSA &rarr; EZ Cloud
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              How each layer of the FIPSA stack maps forward in a modernization. The business logic
              is preserved. The maintenance burden is not.
            </p>
            <div className="overflow-x-auto">
              <table className="w-full border border-gray-200 rounded-xl overflow-hidden">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="text-left text-xs font-bold uppercase tracking-wide text-accent px-5 py-4 w-1/4">
                      Layer
                    </th>
                    <th className="text-left text-xs font-bold uppercase tracking-wide text-accent px-5 py-4 w-3/8">
                      FIPSA today
                    </th>
                    <th className="text-left text-xs font-bold uppercase tracking-wide text-accent px-5 py-4 w-3/8">
                      EZ Cloud modernized
                    </th>
                  </tr>
                </thead>
                <tbody className="text-sm">
                  {MODERNIZATION_MAPPING.map((row) => (
                    <tr key={row.layer} className="border-b border-gray-200 last:border-b-0">
                      <td className="px-5 py-4 font-semibold text-gray-900 bg-gray-50">
                        {row.layer}
                      </td>
                      <td className="px-5 py-4 text-gray-700">{row.fipsa}</td>
                      <td className="px-5 py-4 text-gray-700">
                        <strong className="font-semibold text-gray-900">{row.modern}</strong>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-center text-sm text-gray-500 mt-6 max-w-2xl mx-auto">
              EZ Cloud preserves the Oracle E-Business Suite integration pattern — same Oracle
              Payables Open Interface. No base-table writes. Audit trail and security model
              preserved.
            </p>
          </div>
        </section>

        {/* Decision Guide CTA */}
        <section className="py-12 bg-accent/5 border-y border-accent/30">
          <div className="container mx-auto px-4 max-w-4xl text-center">
            <p className="text-xs font-bold uppercase tracking-wide text-accent mb-2">
              Free resource
            </p>
            <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
              The Oracle WebCenter AP December 2026 Decision Guide
            </h3>
            <p className="text-gray-700 leading-relaxed mb-6 max-w-2xl mx-auto">
              The structured guide to the four forward paths, stack-specific considerations
              (including FIPSA), eight diagnostic questions, and vendor evaluation checklist.
            </p>
            <Link
              to="/resources/webcenter-decision-guide"
              className="inline-flex items-center font-semibold text-white bg-accent hover:bg-accent/90 transition-colors px-6 py-3 rounded-lg"
            >
              Read the Decision Guide
              <ArrowRight className="w-4 h-4 ml-2" aria-hidden />
            </Link>
          </div>
        </section>

        {/* Cross-link to hub */}
        <section className="py-12 bg-white">
          <div className="container mx-auto px-4 max-w-4xl">
            <Card className="p-8 md:p-10 bg-accent/5 border border-accent/30 shadow-sm">
              <p className="text-xs font-bold uppercase tracking-wide text-accent mb-3">
                The full modernization path
              </p>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                See how EZ Cloud handles the full Oracle WebCenter AP stack
              </h3>
              <p className="text-gray-700 leading-relaxed mb-5">
                FIPSA is one of the WebCenter AP patterns we modernize. The Modernize Oracle
                WebCenter AP page covers the full stack — Forms Recognition, SOA, WebLogic, FIPSA,
                ADF — across 11g, 12c, and 14c. Deploys on Oracle E-Business Suite or Oracle Fusion
                Cloud ERP.
              </p>
              <Link
                to="/solutions/modernize-oracle-webcenter"
                className="inline-flex items-center font-semibold text-primary hover:text-accent transition-colors"
              >
                See the full WebCenter AP modernization path
                <ArrowRight className="w-4 h-4 ml-1" aria-hidden />
              </Link>
            </Card>
          </div>
        </section>

        <WebCenterEngagementHistory />

        {/* FAQ */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4 max-w-3xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              Common questions
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-10">
              Direct answers on FIPSA modernization
            </h2>
            <Accordion type="single" collapsible className="w-full">
              {FAQS.map((f, i) => (
                <AccordionItem key={f.q} value={`item-${i}`}>
                  <AccordionTrigger className="text-left text-gray-900">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-gray-600">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 bg-hero-gradient text-white">
          <div className="container mx-auto px-4 max-w-3xl text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Let's read your FIPSA implementation directly
            </h2>
            <p className="text-xl text-white/80 mb-8 leading-relaxed">
              A 30-minute call with {FOUNDER.name}, founder of EZ Cloud and a 25-year Oracle
              WebCenter AP specialist. We'll talk through your FIPSA footprint and what
              modernization looks like for your specific implementation.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button
                size="lg"
                variant="accent"
                className="font-semibold"
                onClick={() => openWebCenterDecisionCall('webcenter-page')}
              >
                <Calendar className="mr-2 w-5 h-5" aria-hidden />
                Book a FIPSA Modernization Call
                <ArrowRight className="ml-2 w-5 h-5" aria-hidden />
              </Button>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default ModernizeWebCenterFipsa;
